import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { useToasts } from 'react-toast-notifications';
import {
  CircularProgress,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';

import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import Card from 'components/Card/Card';
import Button from 'components/CustomButtons/Button.js';

import api from '../../services/api';

const useStyles = makeStyles(styles);

const getFirstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const getDisplayText = value => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(getDisplayText).filter(Boolean).join(', ');
  }

  if (typeof value === 'object') {
    return getFirstValue(
      getDisplayText(value?.name),
      getDisplayText(value?.nome),
      getDisplayText(value?.title),
      getDisplayText(value?.descricao),
      getDisplayText(value?.description),
      getDisplayText(value?.label),
      getDisplayText(value?.value),
      getDisplayText(value?.color),
      '',
    );
  }

  return '';
};

const getAttemptMessageText = value => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const textValue = String(value).trim();

    if (
      typeof value === 'string' &&
      textValue &&
      (textValue.startsWith('{') || textValue.startsWith('['))
    ) {
      try {
        return getAttemptMessageText(JSON.parse(textValue));
      } catch (e) {}
    }

    return textValue;
  }

  if (Array.isArray(value)) {
    return value.map(getAttemptMessageText).filter(Boolean).join(' | ');
  }

  if (typeof value === 'object') {
    return (
      getFirstValue(
        getAttemptMessageText(value?.message),
        getAttemptMessageText(value?.error_message),
        getAttemptMessageText(value?.errorMessage),
        getAttemptMessageText(value?.description),
        getAttemptMessageText(value?.detail),
        getAttemptMessageText(value?.details),
        getAttemptMessageText(value?.reason),
        getAttemptMessageText(value?.response),
        getAttemptMessageText(value?.data),
        '',
      ) || ''
    );
  }

  return '';
};

const toArray = value => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.rows)) {
    return value.rows;
  }

  if (Array.isArray(value?.results)) {
    return value.results;
  }

  return [];
};

const parseNumber = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.replace(/\s/g, '').replace(/[^\d,.-]/g, '');
    const hasComma = trimmedValue.includes(',');
    const normalized = hasComma
      ? trimmedValue.replace(/\./g, '').replace(',', '.')
      : trimmedValue.replace(/,/g, '');

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parseNumber(value));

const formatDate = value => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getCustomerName = source => {
  const customer = getFirstValue(
    source?.customer,
    source?.cliente,
    source?.user,
  );

  return (
    getDisplayText(
      getFirstValue(
        customer?.name,
        source?.customer_name,
        source?.cliente_nome,
        source?.name,
      ),
    ) || '-'
  );
};

const getCustomerEmail = source => {
  const customer = getFirstValue(
    source?.customer,
    source?.cliente,
    source?.user,
  );

  return (
    getDisplayText(
      getFirstValue(
        customer?.email,
        source?.customer_email,
        source?.cliente_email,
        source?.email,
      ),
    ) || '-'
  );
};

const getCustomerPhone = source => {
  const customer = getFirstValue(
    source?.customer,
    source?.cliente,
    source?.user,
  );

  return (
    getDisplayText(
      getFirstValue(
        customer?.tel_01,
        customer?.phone,
        customer?.telefone,
        customer?.telefone1,
        customer?.phone_1,
        customer?.celular,
        customer?.whatsapp,
        source?.customer_phone,
        source?.cliente_telefone,
        source?.phone,
        source?.telefone,
        source?.telefone1,
        source?.tel_01,
      ),
    ) || '-'
  );
};

const getItemQuantity = item =>
  parseNumber(getFirstValue(item?.quantity, item?.qtde, item?.amount, 0));

const getItemUnitPrice = item =>
  parseNumber(
    getFirstValue(
      item?.unitPrice,
      item?.unit_price,
      item?.price,
      item?.valor,
      item?.value,
      item?.unit_value,
      0,
    ),
  );

const getItemSubtotal = item => {
  const explicitSubtotal = getFirstValue(
    item?.subtotal,
    item?.total,
    item?.valor_total,
    item?.total_value,
  );

  if (
    explicitSubtotal !== undefined &&
    explicitSubtotal !== null &&
    explicitSubtotal !== ''
  ) {
    return parseNumber(explicitSubtotal);
  }

  return getItemQuantity(item) * getItemUnitPrice(item);
};

const getItemName = item => {
  const product = getFirstValue(item?.product, item?.produto);

  return (
    getDisplayText(
      getFirstValue(
        product?.name,
        product?.nome,
        item?.product_name,
        item?.produto_nome,
        item?.name,
        item?.nome,
        item?.title,
        item?.descricao,
        product,
      ),
    ) || '-'
  );
};

const getItemVariation = item => {
  return (
    getDisplayText(
      getFirstValue(
        item?.variation,
        item?.variant,
        item?.color?.name,
        item?.color,
        item?.cor?.name,
        item?.cor,
        item?.measure,
        item?.size,
        item?.largura,
        item?.descricao_variacao,
      ),
    ) || '-'
  );
};

const getItemsCountFromSource = source => {
  const explicitCount = getFirstValue(
    source?.itemsCount,
    source?.items_count,
    source?.items_count_total,
    source?.item_count,
    source?.itemCount,
    source?.numero_itens,
    source?.numero_de_itens,
    source?.qtd_itens,
    source?.qtde_itens,
    source?.quantidade_itens,
    source?.total_items,
    source?.total_itens,
    source?.totalItems,
    source?.itens_total,
    source?.products_count,
    source?.produtos_count,
  );

  if (
    explicitCount !== undefined &&
    explicitCount !== null &&
    explicitCount !== ''
  ) {
    return parseNumber(explicitCount);
  }

  return null;
};

const getTotalValueFromSource = source => {
  const explicitTotal = getFirstValue(
    source?.totalValue,
    source?.total_value,
    source?.total,
    source?.valor_total,
    source?.value,
    source?.valor,
    source?.cart_total,
    source?.cart_value,
  );

  if (
    explicitTotal !== undefined &&
    explicitTotal !== null &&
    explicitTotal !== ''
  ) {
    return parseNumber(explicitTotal);
  }

  return null;
};

const normalizePaymentAttempt = attempt => ({
  id: getFirstValue(
    attempt?.id,
    `${getFirstValue(
      attempt?.created_at,
      attempt?.updated_at,
      '',
    )}-${getFirstValue(attempt?.order_ref, attempt?.forma_pg, '')}`,
  ),
  createdAt: getFirstValue(attempt?.created_at, attempt?.updated_at),
  orderRef:
    getDisplayText(
      getFirstValue(attempt?.order_ref, attempt?.orderRef, attempt?.order_id),
    ) || '-',
  paymentMethod:
    getDisplayText(
      getFirstValue(
        attempt?.forma_pg,
        attempt?.payment_method,
        attempt?.paymentMethod,
      ),
    ) || '-',
  installments: getFirstValue(attempt?.parcelas, attempt?.installments, '-'),
  itemsCount: getFirstValue(
    attempt?.items_count,
    attempt?.itemsCount,
    attempt?.item_count,
    '-',
  ),
  amount: parseNumber(
    getFirstValue(
      attempt?.amount,
      attempt?.total_value,
      attempt?.totalValue,
      attempt?.value,
      0,
    ),
  ),
  attemptStatus:
    getDisplayText(
      getFirstValue(attempt?.attempt_status, attempt?.attemptStatus),
    ) || '-',
  paymentStatus:
    getDisplayText(
      getFirstValue(attempt?.payment_status, attempt?.paymentStatus),
    ) || '-',
  message:
    getAttemptMessageText(
      getFirstValue(
        attempt?.message,
        attempt?.error_message,
        attempt?.errorMessage,
      ),
    ) || '',
});

const getPaymentAttemptsFromPayload = source => {
  const attempts = getFirstValue(
    source?.payment_attempts,
    source?.paymentAttempts,
    source?.attempts,
    source?.tentativas_pagamento,
    source?.data?.payment_attempts,
    source?.data?.paymentAttempts,
    source?.data?.attempts,
  );

  return Array.isArray(attempts) ? attempts.map(normalizePaymentAttempt) : [];
};

const normalizeCartItem = item => ({
  id: getFirstValue(
    item?.id,
    item?.product_id,
    item?.produto_id,
    `${getItemName(item)}-${getItemVariation(item)}-${getItemQuantity(item)}`,
  ),
  name: getItemName(item),
  variation: getItemVariation(item),
  quantity: getItemQuantity(item),
  unitPrice: getItemUnitPrice(item),
  subtotal: getItemSubtotal(item),
});

const buildCartSummaryFromItems = (session, items) => {
  const firstItem = items[0] || {};

  const normalizedItems = items.map(normalizeCartItem);
  const calculatedItemsCount = normalizedItems.reduce(
    (total, item) => total + parseNumber(item.quantity),
    0,
  );
  const calculatedTotalValue = normalizedItems.reduce(
    (total, item) => total + parseNumber(item.subtotal),
    0,
  );
  const itemsCount =
    getItemsCountFromSource(firstItem) ??
    calculatedItemsCount ??
    normalizedItems.length;
  const totalValue = getTotalValueFromSource(firstItem) ?? calculatedTotalValue;

  return {
    id: getFirstValue(firstItem?.id, session),
    session,
    created_at: getFirstValue(
      firstItem?.created_at,
      firstItem?.updated_at,
      firstItem?.date,
    ),
    customerName: getCustomerName(firstItem),
    customerEmail: getCustomerEmail(firstItem),
    customerPhone: getCustomerPhone(firstItem),
    itemsCount,
    totalValue,
    items: normalizedItems,
    paymentAttempts: [],
  };
};

const normalizeSummaryCart = cart => {
  const session = String(
    getFirstValue(cart?.session, cart?.session_id, cart?.id, ''),
  );
  const rawItems = toArray(
    getFirstValue(cart?.items, cart?.products, cart?.itens),
  );
  const normalizedItems = rawItems.map(normalizeCartItem);

  const itemsCountFromPayload = getItemsCountFromSource(cart);
  const totalValueFromPayload = getTotalValueFromSource(cart);

  const itemsCount =
    itemsCountFromPayload !== undefined && itemsCountFromPayload !== null
      ? parseNumber(itemsCountFromPayload)
      : normalizedItems.reduce(
          (total, item) => total + parseNumber(item.quantity),
          0,
        );

  const totalValue =
    totalValueFromPayload !== undefined && totalValueFromPayload !== null
      ? parseNumber(totalValueFromPayload)
      : normalizedItems.reduce(
          (total, item) => total + parseNumber(item.subtotal),
          0,
        );

  return {
    id: getFirstValue(cart?.id, session),
    session,
    created_at: getFirstValue(cart?.created_at, cart?.updated_at, cart?.date),
    customerName: getCustomerName(cart),
    customerEmail: getCustomerEmail(cart),
    customerPhone: getCustomerPhone(cart),
    itemsCount,
    totalValue,
    items: normalizedItems,
    paymentAttempts: getPaymentAttemptsFromPayload(cart),
  };
};

const normalizeListResponse = payload => {
  const data = toArray(payload);

  if (!data.length) {
    return [];
  }

  const hasEmbeddedItems = data.some(cart =>
    Array.isArray(getFirstValue(cart?.items, cart?.products, cart?.itens)),
  );
  const hasSummaryMetrics = data.some(
    cart =>
      getItemsCountFromSource(cart) !== null ||
      getTotalValueFromSource(cart) !== null,
  );

  if (hasEmbeddedItems || hasSummaryMetrics) {
    return data.map(normalizeSummaryCart);
  }

  const groupedBySession = data.reduce((accumulator, item) => {
    const session = String(
      getFirstValue(item?.session, item?.session_id, item?.id, ''),
    );

    if (!session) {
      return accumulator;
    }

    if (!accumulator[session]) {
      accumulator[session] = [];
    }

    accumulator[session].push(item);
    return accumulator;
  }, {});

  return Object.entries(groupedBySession).map(([session, items]) =>
    buildCartSummaryFromItems(session, items),
  );
};

const normalizeDetailResponse = (session, payload, fallbackSummary) => {
  const data = toArray(payload);
  const paymentAttempts = getPaymentAttemptsFromPayload(payload);

  if (data.length) {
    const detailedCart = buildCartSummaryFromItems(session, data);

    return {
      ...fallbackSummary,
      ...detailedCart,
      customerName: getFirstValue(
        detailedCart.customerName,
        fallbackSummary?.customerName,
        '-',
      ),
      customerEmail: getFirstValue(
        detailedCart.customerEmail,
        fallbackSummary?.customerEmail,
        '-',
      ),
      customerPhone: getFirstValue(
        detailedCart.customerPhone,
        fallbackSummary?.customerPhone,
        '-',
      ),
      paymentAttempts:
        paymentAttempts.length > 0
          ? paymentAttempts
          : fallbackSummary?.paymentAttempts || [],
    };
  }

  const summaryCandidate = getFirstValue(payload?.data, payload);

  if (summaryCandidate && typeof summaryCandidate === 'object') {
    const normalized = normalizeSummaryCart({
      ...summaryCandidate,
      session,
    });

    return {
      ...fallbackSummary,
      ...normalized,
      customerName: getFirstValue(
        normalized.customerName,
        fallbackSummary?.customerName,
        '-',
      ),
      customerEmail: getFirstValue(
        normalized.customerEmail,
        fallbackSummary?.customerEmail,
        '-',
      ),
      customerPhone: getFirstValue(
        normalized.customerPhone,
        fallbackSummary?.customerPhone,
        '-',
      ),
      paymentAttempts:
        paymentAttempts.length > 0
          ? paymentAttempts
          : normalized.paymentAttempts ||
            fallbackSummary?.paymentAttempts ||
            [],
    };
  }

  return {
    ...fallbackSummary,
    paymentAttempts:
      paymentAttempts.length > 0
        ? paymentAttempts
        : fallbackSummary?.paymentAttempts || [],
  };
};

export default function ShoppingCarts() {
  const classes = useStyles();
  const { addToast } = useToasts();

  const [loading, setLoading] = useState(true);
  const [carts, setCarts] = useState([]);
  const [selectedCart, setSelectedCart] = useState(null);
  const [isDetailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadCarts = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get('/cart/identified');
      const normalizedCarts = normalizeListResponse(response.data);

      normalizedCarts.sort((firstCart, secondCart) => {
        const firstDate = new Date(firstCart.created_at).getTime() || 0;
        const secondDate = new Date(secondCart.created_at).getTime() || 0;

        return secondDate - firstDate;
      });

      setCarts(normalizedCarts);
    } catch (e) {
      setCarts([]);
      addToast(
        e.response?.data?.message ||
          'Nao foi possivel carregar os carrinhos perdidos.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadCarts();
  }, [loadCarts]);

  const handleOpenDetails = useCallback(
    async cart => {
      try {
        setSelectedCart(cart);
        setDetailsOpen(true);

        if (!cart.session) {
          return;
        }

        setDetailsLoading(true);

        const response = await api.get(
          `/cart/identified/${encodeURIComponent(cart.session)}`,
        );

        const normalizedDetails = normalizeDetailResponse(
          cart.session,
          response.data,
          cart,
        );

        setSelectedCart(normalizedDetails);
      } catch (e) {
        addToast(
          e.response?.data?.message ||
            'Nao foi possivel carregar os detalhes do carrinho perdido.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
      } finally {
        setDetailsLoading(false);
      }
    },
    [addToast],
  );

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedCart(null);
    setDetailsLoading(false);
  }, []);

  const selectedCartItems = useMemo(() => {
    return Array.isArray(selectedCart?.items) ? selectedCart.items : [];
  }, [selectedCart]);

  const selectedCartPaymentAttempts = useMemo(() => {
    return Array.isArray(selectedCart?.paymentAttempts)
      ? selectedCart.paymentAttempts
      : [];
  }, [selectedCart]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.cardTitleWhite}>Carrinho Perdido</h4>
            <p className={classes.cardCategoryWhite}>
              Carrinhos identificados que ainda nao foram vinculados a pedido.
            </p>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Box mt={6}>
                <CircularProgress />
              </Box>
            ) : carts.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Numero de itens</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell align="right">Detalhes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carts.map(cart => (
                    <TableRow key={cart.session || cart.id}>
                      <TableCell>{formatDate(cart.created_at)}</TableCell>
                      <TableCell>
                        <strong>{cart.customerName}</strong>
                      </TableCell>
                      <TableCell>{cart.customerEmail}</TableCell>
                      <TableCell>{cart.customerPhone}</TableCell>
                      <TableCell>{cart.itemsCount}</TableCell>
                      <TableCell>{formatCurrency(cart.totalValue)}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="info"
                          type="button"
                          onClick={() => handleOpenDetails(cart)}
                        >
                          <Visibility /> Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p style={{ marginTop: 20 }}>
                Nenhum carrinho perdido encontrado no momento.
              </p>
            )}
          </CardBody>
        </Card>
      </GridItem>

      <Dialog
        open={isDetailsOpen}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedCart ? `Detalhes do carrinho` : 'Detalhes do carrinho'}
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box mt={2} mb={2}>
              <CircularProgress />
            </Box>
          ) : selectedCart ? (
            <>
              <Box mb={3}>
                <p>
                  <strong>Data:</strong>{' '}
                  {formatDateTime(selectedCart.created_at)}
                </p>
                <p>
                  <strong>Cliente:</strong> {selectedCart.customerName}
                </p>
                <p>
                  <strong>Email:</strong> {selectedCart.customerEmail}
                </p>
                <p>
                  <strong>Telefone:</strong> {selectedCart.customerPhone}
                </p>
                <p>
                  <strong>Numero de itens:</strong> {selectedCart.itemsCount}
                </p>
                <p>
                  <strong>Valor total:</strong>{' '}
                  {formatCurrency(selectedCart.totalValue)}
                </p>
              </Box>

              <h4 style={{ marginTop: 24, marginBottom: 12 }}>
                Tentativas de Pagamento
              </h4>

              {selectedCartPaymentAttempts.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Ref.</TableCell>
                      <TableCell>Forma Pg</TableCell>
                      <TableCell>Parcelas</TableCell>
                      <TableCell>Itens</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Status tentativa</TableCell>
                      <TableCell>Status pg</TableCell>
                      <TableCell>Info</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCartPaymentAttempts.map(attempt => (
                      <TableRow key={attempt.id}>
                        <TableCell>
                          {formatDateTime(attempt.createdAt)}
                        </TableCell>
                        <TableCell>{attempt.orderRef}</TableCell>
                        <TableCell>{attempt.paymentMethod}</TableCell>
                        <TableCell>{attempt.installments}</TableCell>
                        <TableCell>{attempt.itemsCount}</TableCell>
                        <TableCell>{formatCurrency(attempt.amount)}</TableCell>
                        <TableCell>{attempt.attemptStatus}</TableCell>
                        <TableCell>{attempt.paymentStatus}</TableCell>
                        <TableCell>{attempt.message || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p>Nenhuma tentativa de pagamento registrada.</p>
              )}

              <h4 style={{ marginTop: 24, marginBottom: 12 }}>
                Itens do Carrinho
              </h4>

              {selectedCartItems.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell>Variacao</TableCell>
                      <TableCell>Qtde</TableCell>
                      <TableCell>Valor unitario</TableCell>
                      <TableCell>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCartItems.map(item => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.variation}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell>{formatCurrency(item.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p>Nenhum item encontrado para este carrinho perdido.</p>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="primary" type="button" onClick={handleCloseDetails}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </GridContainer>
  );
}
